// Face matching + liveness helpers for Nexus Eye (all on-device).
export const STRICT = 0.42;   // below = confident match
export const MAYBE = 0.52;    // between STRICT and MAYBE = "maybe" (never treated as the person, never a stranger)
export const MARGIN = 0.06;   // best person must beat the runner-up person by this much

const dist = (a, b) => { let s = 0; for (let i = 0; i < 128; i++) { const d = a[i] - b[i]; s += d * d; } return Math.sqrt(s); };

export const identify = (desc, faces) => {
  const per = faces.map((f) => ({ name: f.name, d: Math.min(9, ...(f.descriptors || []).map((x) => dist(desc, x))) })).sort((a, b) => a.d - b.d);
  const first = (n) => String(n || "").trim().toLowerCase().split(/\s+/)[0];
  const best = per[0], second = per.find((p) => best && first(p.name) !== first(best.name)); // "Sohel" vs "sohel patel" = same person, not a rival
  if (!best) return { kind: "unknown", d: 9 };
  const conf = Math.max(0, Math.min(1, 1 - best.d / 0.6));
  if (best.d < STRICT && (!second || second.d - best.d >= MARGIN)) return { kind: "known", name: best.name, conf, d: best.d };
  if (best.d < MAYBE) return { kind: "maybe", name: best.name, conf, d: best.d };
  return { kind: "unknown", conf, d: best.d };
};

const ear = (p) => (Math.hypot(p[1].x - p[5].x, p[1].y - p[5].y) + Math.hypot(p[2].x - p[4].x, p[2].y - p[4].y)) / (2 * Math.hypot(p[0].x - p[3].x, p[0].y - p[3].y));

// yaw: -left .. +right (nose offset vs eye midpoint, normalised by eye distance); pitch: nose height vs eyes/mouth
export const pose = (lm) => {
  const p = lm.positions;
  const le = p.slice(36, 42), re = p.slice(42, 48);
  const eyeL = { x: le.reduce((s, q) => s + q.x, 0) / 6, y: le.reduce((s, q) => s + q.y, 0) / 6 };
  const eyeR = { x: re.reduce((s, q) => s + q.x, 0) / 6, y: re.reduce((s, q) => s + q.y, 0) / 6 };
  const mid = { x: (eyeL.x + eyeR.x) / 2, y: (eyeL.y + eyeR.y) / 2 };
  const span = Math.hypot(eyeR.x - eyeL.x, eyeR.y - eyeL.y) || 1;
  const nose = p[30], mouth = p[62];
  return { yaw: (nose.x - mid.x) / span, pitch: (nose.y - mid.y) / (Math.abs(mouth.y - mid.y) || 1), ear: (ear(le) + ear(re)) / 2 };
};

// Liveness: a blink (eye-aspect-ratio dip and recovery) or a real head turn within the last 15 s.
export const makeLiveness = () => {
  const s = { open: null, closedAt: 0, yaws: [], liveUntil: 0 };
  return (lm) => {
    const now = Date.now();
    const { yaw, ear: e } = pose(lm);
    s.open = s.open == null ? e : Math.max(s.open * 0.98, e);
    if (e < s.open * 0.7) s.closedAt = now;
    else if (s.closedAt && now - s.closedAt < 1500 && e > s.open * 0.85) { s.liveUntil = now + 15000; s.closedAt = 0; }
    s.yaws = [...s.yaws.filter((y) => now - y.t < 4000), { t: now, v: yaw }];
    const ys = s.yaws.map((y) => y.v);
    if (ys.length > 3 && Math.max(...ys) - Math.min(...ys) > 0.35) s.liveUntil = now + 15000;
    return now < s.liveUntil;
  };
};

export const POSES = [
  { id: "straight", label: "Look straight at the camera", ok: (p) => Math.abs(p.yaw) < 0.12 && p.pitch > 0.3 && p.pitch < 0.75 },
  { id: "left", label: "Turn your head slowly to YOUR left", ok: (p) => p.yaw > 0.15 },
  { id: "right", label: "Turn your head slowly to YOUR right", ok: (p) => p.yaw < -0.15 },
  { id: "up", label: "Tilt your chin up a little", ok: (p) => p.pitch < 0.42 },
  { id: "down", label: "Tilt your chin down a little", ok: (p) => p.pitch > 0.62 },
];
